import { IconCancel } from '@tabler/icons-react-native'
import { clsx } from 'clsx'
import { ComponentRef, useCallback, useRef, type FC } from 'react'
import { graphql, useFragment } from 'react-relay'

import { type ProfileBlockButton_profile$key } from '@/__generated__/ProfileBlockButton_profile.graphql'
import { Dialog, ProfileButton, type ProfileButtonProps } from '@/components'
import { useBlockMutation, useDisclosure } from '@/hooks'

export interface ProfileBlockButtonProps extends Omit<ProfileButtonProps, 'label' | 'onPress'> {
  confirmTestID?: string
  label?: string
  onConfirm?: () => void
  profileRef: ProfileBlockButton_profile$key | null | undefined
}

export const ProfileBlockButton: FC<ProfileBlockButtonProps> = ({
  className,
  confirmTestID,
  isDisabled: _isDisabled,
  label = 'Block',
  onConfirm,
  profileRef,
  ...props
}) => {
  const buttonRef = useRef<ComponentRef<typeof ProfileButton>>(null)
  const profileData = useFragment(
    graphql`
      fragment ProfileBlockButton_profile on Profile {
        name
        userId
      }
    `,
    profileRef
  )
  const [block, isBlocking] = useBlockMutation()
  const [showBlockDialog, blockDialog] = useDisclosure(false)
  const isDisabled = _isDisabled || isBlocking

  return (
    <>
      <ProfileButton
        accessibilityLabel={`Block ${profileData?.name ?? 'this profile'}`}
        className={clsx(className)}
        Icon={IconCancel}
        isDisabled={isDisabled}
        label={label}
        onPress={blockDialog.open}
        ref={buttonRef}
        {...props}
      />
      <Dialog
        finalFocusRef={buttonRef}
        isDisabled={isDisabled}
        isOpen={showBlockDialog}
        confirmLabel='Block'
        confirmTestID={confirmTestID}
        onClose={blockDialog.close}
        onConfirm={useCallback(async () => {
          await block(profileData?.userId)
          onConfirm?.()
        }, [block, onConfirm, profileData?.userId])}
        title={`Block ${profileData?.name ?? 'Profile'}`}
      >
        {`Are you sure you want to block ${profileData?.name ?? 'this profile'}?`}
      </Dialog>
    </>
  )
}
